import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, ButtonGroup } from '@mui/material';
import clsx from 'clsx';
import {
  Badge,
  badgeStyle,
  badgeType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Icons,
  Input,
  inputType,
} from '../../../packages';

import { stateSelector } from '../../../redux/modules/state/selectors';
import { stateActionCreators } from '../../../redux/modules/state';

import { BaseLayout } from '../../../layouts/base';

import { StateCard } from './state-card';
import { AddStateDrawer } from './add-state';
import { StatesTabs } from './States.constants';
import { useStyles } from './States.styles';

const States = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isLoading, states } = useSelector(stateSelector);
  const [statesFiltered, setStatesFiltered] = useState(states);
  const [selectedType, setSelectedType] = useState('all');
  const [searchKey, setSearchKey] = useState('');
  const [selectedState, setSelectedState] = useState();
  const [openStateDrawer, setOpenStateDrawer] = useState(false);

  const onChangeType = type => () => {
    setSelectedType(type);
  };

  const onClickUpdate = item => {
    setSelectedState(item);
    setOpenStateDrawer(true);
  };

  const onSubmit = payload => {
    dispatch(
      stateActionCreators.updateState({
        bodyRequest: payload,
        pathParams: { stateId: selectedState.state._id },
        callback: () => {
          setOpenStateDrawer(false);
          dispatch(stateActionCreators.fetchStates());
        },
      }),
    );
  };

  useEffect(() => {
    let newList = [];
    switch (selectedType) {
      case StatesTabs[1].type:
        newList = states?.filter(
          item =>
            item?.state?.isActive && item?.state?.name.toLocaleLowerCase().startsWith(searchKey?.charAt(0)),
        );
        break;
      case StatesTabs[2].type:
        newList = states?.filter(
          item =>
            !item?.state?.isActive && item?.state?.name?.toLocaleLowerCase().startsWith(searchKey?.charAt(0)),
        );
        break;
      default:
        newList = states?.filter(item =>
          item?.state?.name?.toLocaleLowerCase().startsWith(searchKey?.charAt(0)),
        );
        break;
    }
    setStatesFiltered(newList);
  }, [states, selectedType, searchKey]);

  useEffect(() => {
    dispatch(stateActionCreators.fetchStates());
  }, []);

  return (
    <BaseLayout>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Box display="flex" alignItems="center" gap={2}>
            <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
              States
            </Heading>
            <Badge className={classes.totalBadge} variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED}>
              {statesFiltered?.length || '0'} total
            </Badge>
          </Box>
          <ButtonGroup variant="outlined" aria-label="outlined button group">
            {StatesTabs.map(({ type, label }) => (
              <Button
                key={type}
                className={clsx(classes.btnGroupIcon, {
                  [classes.btnGroupIconActive]: type === selectedType,
                })}
                onClick={onChangeType(type)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        <AddStateDrawer
          isOpen={openStateDrawer}
          selectedState={selectedState}
          onSubmit={onSubmit}
          onClose={() => setOpenStateDrawer(false)}
        />
        <Box gap="16px" display="flex" flexDirection="column">
          <Input
            className={classes.searchInput}
            variant={inputType.SEARCH}
            placeholder="Search"
            value={searchKey}
            onChange={setSearchKey}
          />

          {isLoading && states.length === 0 ? (
            <Box display="flex" justifyContent="center" sx={{ paddingY: 5 }}>
              <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            </Box>
          ) : (
            <>
              {statesFiltered.length ? (
                statesFiltered.map((item, index) => (
                  <StateCard key={index} item={item} onClickUpdate={() => onClickUpdate(item)} />
                ))
              ) : (
                <>No states available</>
              )}
            </>
          )}
        </Box>
      </Box>
    </BaseLayout>
  );
};

export { States };
